/**
 * QL-107 — the fixed-buffer trigger, shared by the API's capacity calculation
 * and the advisor-facing live preview so the two can never disagree.
 *
 * Pure: no I/O, no throws. The caller reads the client's stored value and
 * hands the raw text in, which keeps `computeCapacity` free of db/env/logger
 * dependencies and keeps this decision unit-testable on its own.
 */

import type { InvestmentCapacityVariantConfig } from "./types/financial-situation-models";

/**
 * The liquidity buffer a fixed-buffer policy imposes on this client, or null
 * when the policy does not apply (no field configured, no usable amount, or
 * the client's flag is not ticked).
 *
 * `client_custom_field_value.value` is a free-form varchar regardless of the
 * field's declared type, so the text→yes/no normalisation happens here and
 * exactly once. It is the strict `=== "true"` the rest of the codebase uses
 * for booleans (flow-validation's `isFieldValueFilled`, the client profile
 * display, and conditional display — whose tests assert that "TRUE", "1",
 * "yes" and " true " are NOT true). Anything else means no.
 */
export function resolveFixedBuffer(
  config:
    | Pick<InvestmentCapacityVariantConfig, "fixedBufferClientField" | "fixedBufferAmount">
    | null
    | undefined,
  storedFieldValue: string | null | undefined,
): number | null {
  // A chosen field IS the enabled state — no field, no fixed buffer.
  if (!config?.fixedBufferClientField) return null;

  const amount = config.fixedBufferAmount;
  if (typeof amount !== "number" || !Number.isFinite(amount) || amount <= 0) return null;

  return storedFieldValue === "true" ? amount : null;
}
